import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Users, Search } from "lucide-react";
import { STATS } from "../data";
import { Counter } from "./ui-blocks";

const LINES = ["We find the right", "people for your", "business."];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.16, 1, 0.3, 1] } }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const orbY = useTransform(scrollYProgress, [0, 1], [0, 200]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center pt-32 pb-16 overflow-hidden" data-testid="hero">
      <motion.div style={{ y: orbY }} className="absolute top-24 -left-20 w-[520px] h-[520px] rounded-full bg-amber-500/15 blur-[130px] animate-orb pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-blue-600/10 blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-mono font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20 mb-7">
              <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400" /></span>
              Trusted by 500+ Companies Across the US
            </motion.div>

            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02] text-white">
              <motion.span initial="hidden" animate="show" className="block">
                {LINES.map((line, i) => (
                  <span key={i} className="mask-line">
                    <motion.span custom={i} variants={lineVariants} className="block">
                      {i === 2 ? <span className="text-gradient-amber">{line}</span> : line}
                    </motion.span>
                  </span>
                ))}
              </motion.span>
            </h1>

            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }} className="mt-7 text-lg sm:text-xl text-slate-400 leading-relaxed max-w-xl">
              Staffing services built for long-term success. From temporary workforce to executive search, we deliver talent that drives your business forward.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.85 }} className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/employers" data-testid="hero-cta-request-staff" className="group inline-flex items-center justify-center gap-2 px-6 py-4 text-sm font-semibold text-slate-950 bg-gradient-to-r from-amber-400 to-amber-500 rounded-xl shadow-lg shadow-amber-500/25 hover:shadow-amber-500/45 transition-all duration-300 active:scale-[0.98]">
                <Users className="w-4 h-4" /> Request Staff <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <Link to="/job-seekers" data-testid="hero-cta-find-jobs" className="group inline-flex items-center justify-center gap-2 px-6 py-4 text-sm font-semibold text-slate-200 border border-slate-700/80 rounded-xl hover:border-slate-500 hover:text-white transition-all duration-300 active:scale-[0.98]">
                <Search className="w-4 h-4" /> Find a Job
              </Link>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1 }} className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              {STATS.slice(0, 3).map((s) => (
                <div key={s.label} data-testid="hero-stat">
                  <div className="font-accent text-2xl sm:text-3xl font-bold text-white"><Counter value={s.value} suffix={s.suffix} /></div>
                  <div className="text-xs text-slate-500 mt-1 leading-tight">{s.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }} className="relative">
              <div className="relative clip-frame overflow-hidden rounded-2xl border border-slate-800">
                <motion.img
                  style={{ y: imgY, scale: imgScale }}
                  src="https://images.unsplash.com/photo-1758518731706-be5d5230e5a5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
                  alt="Hirezen team collaboration"
                  className="w-full h-[520px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/20 to-transparent" />
              </div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.7 }} className="absolute -bottom-5 -left-5 bg-[#111827] border border-slate-800 rounded-2xl p-5 shadow-2xl shadow-black/40 backdrop-blur-md">
                <div className="font-accent text-3xl font-bold text-amber-400">48hr</div>
                <div className="text-xs text-slate-400 mt-1">Avg. requirement<br />to shortlist</div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
